import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { projectApi, roleLabel, errText } from "../../lib/projectAccess";

const field = "min-h-[44px] w-full rounded border border-erf-borderCream bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-erf-gold";

// Only people and project roles the caller may grant are offered (from /assignable-users).
export default function AddMemberForm({ projectId, assignable, people, onAdded }) {
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const live = useMemo(() => new Set(people.filter((p) => p.status !== "removed").map((p) => `${p.user_id}:${p.role_on_project}`)), [people]);
  const options = (assignable.users || []).filter((u) => !live.has(`${u.id}:${u.role_on_project}`));
  const picked = options.find((u) => u.id === userId);

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) return;
    setBusy(true);
    try {
      await projectApi.add(projectId, { user_id: picked.id, role_on_project: picked.role_on_project, scope: {} });
      toast.success(`${picked.display_name || "Member"} added as ${roleLabel(picked.role_on_project).toLowerCase()}`);
      setUserId("");
      onAdded();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-erf-cream border border-erf-borderCream rounded-md p-3 sm:p-4 grid gap-3 sm:flex sm:flex-wrap sm:items-end"
      data-testid="project-team-add-form">
      <label htmlFor="project-team-add-user" className="grid gap-1 text-xs font-nav text-erf-dark/80 sm:flex-1 sm:min-w-[16rem]">
        Add a person
        <select id="project-team-add-user" className={field} value={userId} onChange={(e) => setUserId(e.target.value)}
          data-testid="project-team-add-user">
          <option value="">{options.length ? "Choose someone…" : "No one else can be added"}</option>
          {options.map((u) => (
            <option key={`${u.id}:${u.role_on_project}`} value={u.id}>
              {u.display_name || "Unnamed user"} — {roleLabel(u.role_on_project)}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" disabled={!picked || busy} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold"
        data-testid="project-team-add-submit">Add to project</Button>
      <p className="text-[11px] text-erf-dark/60 sm:basis-full" data-testid="project-team-add-roles">
        You can add: {(assignable.allowed_roles || []).map(roleLabel).join(", ") || "nobody"}.
      </p>
    </form>
  );
}
